import React, { useState, useRef, useEffect, useCallback } from 'react';
import './DateRangePicker.css';

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (val: string) => void;
  onDateToChange: (val: string) => void;
  onApply?: (from: string, to: string) => void;
  placeholder?: string;
}

const formatDisplay = (date: string): string => {
  if (!date) return '';
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateString = (year: number, month: number, day: number): string => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onApply,
}) => {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState<string>('');
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = useCallback((dateStr: string) => {
    if (!dateFrom || (dateFrom && dateTo)) {
      // Start fresh selection
      onDateFromChange(dateStr);
      onDateToChange('');
    } else {
      // Second click: set end, ensure order, then commit the full range
      let finalFrom = dateFrom;
      let finalTo = dateStr;
      if (dateStr < dateFrom) {
        finalFrom = dateStr;
        finalTo = dateFrom;
      }
      onDateFromChange(finalFrom);
      onDateToChange(finalTo);
      onApply?.(finalFrom, finalTo);
      setOpen(false);
    }
  }, [dateFrom, dateTo, onDateFromChange, onDateToChange, onApply]);

  const isInRange = (dateStr: string) => {
    const start = dateFrom;
    const end = dateTo || hoverDate;
    if (!start || !end) return false;
    const [s, e] = start <= end ? [start, end] : [end, start];
    return dateStr > s && dateStr < e;
  };

  const isRangeStart = (dateStr: string) => dateStr === dateFrom;
  const isRangeEnd = (dateStr: string) => dateStr === dateTo;

  const renderCalendar = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const cells: React.ReactNode[] = [];

    // Blank leading cells
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`blank-${i}`} className="drp-cell drp-cell--empty" />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toDateString(year, month, d);
      const isStart = isRangeStart(dateStr);
      const isEnd = isRangeEnd(dateStr);
      const inRange = isInRange(dateStr);
      const isToday = dateStr === toDateString(today.getFullYear(), today.getMonth(), today.getDate());

      cells.push(
        <div
          key={dateStr}
          className={[
            'drp-cell',
            isStart ? 'drp-cell--start' : '',
            isEnd ? 'drp-cell--end' : '',
            inRange ? 'drp-cell--in-range' : '',
            isToday ? 'drp-cell--today' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => handleDayClick(dateStr)}
          onMouseEnter={() => setHoverDate(dateStr)}
          onMouseLeave={() => setHoverDate('')}
        >
          <span className="drp-cell-inner">{d}</span>
        </div>
      );
    }

    return cells;
  };

  const displayText = () => {
    if (!dateFrom && !dateTo) return 'Select date range';
    if (dateFrom && !dateTo) return `${formatDisplay(dateFrom)} → ...`;
    return `${formatDisplay(dateFrom)} – ${formatDisplay(dateTo)}`;
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDateFromChange('');
    onDateToChange('');
    onApply?.('', '');
    setHoverDate('');
  };

  return (
    <div className="drp-root" ref={wrapperRef}>
      <button
        className={`drp-trigger ${open ? 'drp-trigger--open' : ''} ${(dateFrom || dateTo) ? 'drp-trigger--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        <svg className="drp-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="drp-trigger-text">{displayText()}</span>
        {(dateFrom || dateTo) && (
          <button className="drp-clear-btn" onClick={clearAll} type="button" title="Clear">
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </button>

      {open && (
        <div className="drp-popover">
          <div className="drp-calendar">
            {/* Month Header */}
            <div className="drp-cal-header">
              <button className="drp-nav-btn" onClick={prevMonth} type="button">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="drp-month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
              <button className="drp-nav-btn" onClick={nextMonth} type="button">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Day Labels */}
            <div className="drp-day-labels">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="drp-day-label">{d}</div>
              ))}
            </div>

            {/* Grid */}
            <div className="drp-grid">
              {renderCalendar(viewYear, viewMonth)}
            </div>
          </div>

          {/* Footer */}
          <div className="drp-popover-footer">
            <span className="drp-hint">
              {!dateFrom ? 'Click to select start date' : !dateTo ? 'Now select end date' : `${formatDisplay(dateFrom)} – ${formatDisplay(dateTo)}`}
            </span>
            <button
              className="drp-done-btn"
              onClick={() => setOpen(false)}
              type="button"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
